import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Banner from "@/components/Banner";
import { Eyebrow, GoldButton } from "@/components/Reveal";
import { SERVICES, PROCESS } from "@/data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      <Banner
        image={service.image}
        alt={`Service ${service.name} — PUREVA`}
        eyebrow="Nos services"
        title={[service.name + "."]}
        text={service.desc}
        testid={`service-detail-title-${service.slug}`}
      >
        <Link
          to="/services"
          data-testid="service-detail-back-link"
          className="inline-flex items-center gap-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-gold transition-colors hover:text-ivory"
        >
          <ArrowLeft className="h-4 w-4" />
          Tous nos services
        </Link>
      </Banner>

      {/* ---------------- PRESTATIONS + PHOTO ---------------- */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
          >
            <Eyebrow>Les prestations proposées</Eyebrow>
            <h2
              data-testid="detail-prestations"
              className="mt-4 font-serif text-3xl leading-tight text-noir sm:text-4xl"
            >
              Ce que comprend ce service
            </h2>
            <ul className="mt-8 space-y-4">
              {service.prestations.map((p) => (
                <li key={p} className="flex items-start gap-3.5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold/60 text-gold">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[15px] leading-relaxed text-noir/80 sm:text-base">
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
          >
            <img
              src={service.image}
              alt={`${service.name} — PUREVA`}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
            <span className="absolute left-0 top-0 h-14 w-px bg-gold" />
            <span className="absolute left-0 top-0 h-px w-14 bg-gold" />
          </motion.div>
        </div>
      </section>

      {/* ---------------- LIEUX + DÉROULEMENT ---------------- */}
      <section className="border-y border-neutral-200 bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
          >
            <Eyebrow>Les types de lieux concernés</Eyebrow>
            <h2
              data-testid="detail-lieux"
              className="mt-4 font-serif text-2xl leading-tight text-noir sm:text-3xl"
            >
              Pour quels espaces ?
            </h2>
            <ul className="mt-7 space-y-3.5">
              {service.lieux.map((l) => (
                <li key={l} className="flex items-center gap-3.5">
                  <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                  <span className="text-[15px] leading-relaxed text-noir/80">
                    {l}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <Eyebrow>Le déroulement de l'intervention</Eyebrow>
            <h2
              data-testid="detail-process"
              className="mt-4 font-serif text-2xl leading-tight text-noir sm:text-3xl"
            >
              Comment ça se passe ?
            </h2>
            <div className="mt-7 space-y-5">
              {PROCESS.map((p) => (
                <div key={p.step} className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/60 bg-[#F4EFE6] font-serif text-sm italic text-[#9a7b3f]">
                    {p.step}
                  </span>
                  <div>
                    <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-noir">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-[13px] leading-relaxed text-neutral-500 sm:text-sm">
                      {p.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------------- INFOS + CTA ---------------- */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28" data-testid="detail-infos">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
          >
            <Eyebrow>Préparer votre demande</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-tight text-noir sm:text-4xl">
              Les informations utiles pour évaluer vos besoins
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-neutral-600 sm:text-base">
              Pour préparer une soumission précise, il nous est utile de
              connaître les éléments suivants :
            </p>
          </motion.div>
          <div className="mx-auto mt-10 grid max-w-2xl gap-4 text-left sm:grid-cols-2">
            {service.infos.map((info, i) => (
              <motion.div
                key={info}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="flex items-start gap-3 rounded-md border border-gold/30 bg-white px-5 py-4"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                <span className="text-[14px] leading-relaxed text-noir/80">
                  {info}
                </span>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="mt-12"
          >
            <GoldButton
              as={Link}
              to={`/contact?service=${service.slug}`}
              data-testid="service-detail-cta"
            >
              Demander une soumission
              <ArrowRight className="h-4 w-4" />
            </GoldButton>
          </motion.div>
        </div>
      </section>
    </>
  );
}
